/**
 * api/client.ts
 * Typed fetch wrapper for the PIT-38 API Worker.
 */
import { useAuth } from '@clerk/clerk-react';
import { useMemo } from 'react';
import type { TaxCalculationResult, TaxJobsResponse } from '@wealix/shared';

// VITE_API_URL is the root of the worker (e.g. https://wealix-api.halil-ural-1905.workers.dev)
// In local dev (no VITE_API_URL) we proxy via Vite's server root.
const API_BASE = import.meta.env.VITE_API_URL ?? '';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function apiFetch<T>(token: string, path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...init.headers,
    },
  });

  if (!res.ok) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(res.status, (body as { error?: string }).error ?? res.statusText);
  }

  return res.json() as Promise<T>;
}

// ─────────────────────────────────────────────
// SSE stream parser (fetch-based, supports Bearer auth)
// ─────────────────────────────────────────────

async function parseSSEStream(
  body: ReadableStream<Uint8Array>,
  eventName: string,
  onData: (data: string) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      let currentEvent = '';
      let currentData = '';
      for (const line of lines) {
        if (line.startsWith('event:')) {
          currentEvent = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          currentData = line.slice(5).trim();
        } else if (line === '') {
          if (currentData) {
            if (currentEvent === eventName) onData(currentData);
            if (currentEvent === 'done') {
              void reader.cancel();
              return;
            }
          }
          currentEvent = '';
          currentData = '';
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

// ─────────────────────────────────────────────
// API methods
// ─────────────────────────────────────────────

export interface ImportJob {
  id: string;
  userId: string;
  taxYear: number;
  source: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface UploadUrlResponse {
  uploadUrl: string;
  r2Key: string;
}

export interface ImportStatusResponse {
  id: string;
  status: string;
  rowCount?: number;
  errorMsg?: string;
  updatedAt: string;
}

export interface ImportJobSSEEvent {
  status: string;
  rowCount?: number;
  errorMsg?: string;
  updatedAt: string;
}

export interface SyncJobSSEEvent {
  status: string;
  rowsProcessed?: number;
  errorMsg?: string;
  completedAt?: string;
}

export interface ImportResultsResponse {
  jobId: string;
  taxYear: number;
  source: string;
  rowCount: number;
  pos22Revenue: number;
  pos23Cost: number;
  pos36Revenue: number;
  pos37Cost: number;
  pos38Income: number;
  pos39Loss: number;
  computedAt: string;
}

// ── Admin types ───────────────────────────────────────────────────────────────

export interface AdminJob {
  id: string;
  userId: string;
  taxYear: number;
  source: string;
  status: string;
  rowCount?: number;
  errorMsg?: string;
  r2Key?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AdminJobsResponse {
  total: number;
  limit: number;
  offset: number;
  jobs: AdminJob[];
}

export interface AdminStatsResponse {
  totalJobs: number;
  totalUsers: number;
  byStatus: Array<{ status: string; count: number }>;
  bySource: Array<{ source: string; count: number }>;
}

// ── Exchange & Portfolio types ────────────────────────────────────────────────

export interface Exchange {
  id: string;
  slug: string;
  name: string;
  logoUrl?: string;
  apiDocUrl?: string;
  requiredCredentials: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UserExchange {
  id: string;
  userId: string;
  exchangeId: string;
  exchangeSlug?: string;
  exchangeName?: string;
  label?: string;
  lastSyncedAt?: string;
  syncStatus: 'never' | 'syncing' | 'ok' | 'error';
  syncErrorMsg?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PortfolioPosition {
  id: string;
  userExchangeId: string;
  assetSymbol: string;
  assetType: 'crypto' | 'equity' | 'etf' | 'cash';
  quantity: number;
  avgBuyPrice?: number;
  currency: string;
  lastPrice?: number;
  lastPriceAt?: string;
  updatedAt: string;
  /** Display currency resolved server-side from the user's profile. */
  displayCurrency: string;
  /** Current value (qty × lastPrice) in displayCurrency. Undefined if no rate cached yet. */
  valueInCurrency?: number;
  /** Cost basis (qty × avgBuyPrice) in displayCurrency. Undefined if no rate or no avgBuyPrice. */
  costInCurrency?: number;
}

export interface PortfolioByExchange {
  exchangeId: string;
  exchangeName: string;
  exchangeSlug: string;
  userExchangeId: string;
  label?: string;
  displayCurrency: string;
  totalValueInCurrency: number;
  totalCostInCurrency: number;
  /** True when the exchange itself provides account-level totals (e.g. T212). */
  hasNativeAccountTotals?: boolean;
  positions: PortfolioPosition[];
}

export interface PortfolioSummary {
  totalPositions: number;
  byAssetType: Record<string, number>;
  totalValueInCurrency: number;
  totalCostInCurrency: number;
  displayCurrency: string;
}

// ── Feature flags ─────────────────────────────────────────────────────────────

export interface FeatureFlag {
  key: string;
  isEnabled: boolean;
  description?: string;
  updatedBy?: string;
  updatedAt: string;
}

// ── Live pricing types ────────────────────────────────────────────────────────

export interface PortfolioLiveResponse {
  positions: PortfolioPosition[];
  /** Per-exchange total value in display currency. For native exchanges (e.g. T212) this uses
   * the exchange-provided account total, matching the dashboard summary/live calculation. */
  exchangeTotals?: Record<string, number>;
}

export interface PortfolioSummaryLive {
  totalPositions: number;
  byAssetType: Record<string, number>;
  totalValueInCurrency: number;
  totalCostInCurrency: number;
  displayCurrency: string;
}

export interface ConnectExchangeRequest {
  exchangeId: string;
  label?: string;
  credentials: Record<string, string>;
}

export interface TestConnectionRequest {
  exchangeId: string;
  credentials: Record<string, string>;
}

export interface TestConnectionResponse {
  ok: boolean;
  error?: string;
}

export interface SyncStatusResponse {
  syncJobId: string;
  status: string;
  completedAt?: string;
  errorMsg?: string;
}

export interface UserProfile {
  userId: string;
  preferredCurrency: string;
  updatedAt: string;
}

export { SUPPORTED_CURRENCIES } from '@wealix/shared';
export type { SupportedCurrency } from '@wealix/shared';
export type { TaxAuditEntry, TaxCalculationResult, PitBox, TaxJobsResponse } from '@wealix/shared';

// CreateExchangeRequest removed — exchanges are predefined in the system.

export interface UpdateExchangeRequest {
  isActive: boolean;
}

// JWT Template adı — Clerk Dashboard → JWT Templates'den alınır.
// Template tanımlıysa Bearer token içinde 'role' claim bulunur;
// backend artık ekstra Clerk API çağrısı yapmaz (hızlı yol).
const JWT_TEMPLATE = import.meta.env.VITE_CLERK_JWT_TEMPLATE ?? 'template';

export function useApiClient() {
  const { getToken } = useAuth();

  async function getBearer(): Promise<string> {
    const token = await getToken({ template: JWT_TEMPLATE });
    if (!token) throw new Error('Not authenticated');
    return token;
  }

  async function createJob(taxYear: number, source: string): Promise<ImportJob> {
    const token = await getBearer();
    return apiFetch<ImportJob>(token, '/api/imports', {
      method: 'POST',
      body: JSON.stringify({ taxYear, source }),
    });
  }

  async function getUploadUrl(jobId: string): Promise<UploadUrlResponse> {
    const token = await getBearer();
    return apiFetch<UploadUrlResponse>(token, `/api/imports/${jobId}/upload-url`, {
      method: 'POST',
    });
  }

  async function uploadFile(uploadUrl: string, file: File): Promise<void> {
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': 'text/csv' },
    });
    if (!res.ok) throw new ApiError(res.status, 'Upload to R2 failed');
  }

  async function commitJob(jobId: string): Promise<void> {
    const token = await getBearer();
    await apiFetch(token, `/api/imports/${jobId}/commit`, { method: 'POST' });
  }

  async function getStatus(jobId: string): Promise<ImportStatusResponse> {
    const token = await getBearer();
    return apiFetch<ImportStatusResponse>(token, `/api/imports/${jobId}/status`);
  }

  async function getResults(jobId: string): Promise<ImportResultsResponse> {
    const token = await getBearer();
    return apiFetch<ImportResultsResponse>(token, `/api/imports/${jobId}/results`);
  }

  function downloadUrl(jobId: string, file: 'summary.csv' | 'audit.csv'): string {
    return `${API_BASE}/api/imports/${jobId}/export/${file}`;
  }

  // ── Admin methods (require admin role) ─────────────────────────────────────

  async function adminGetJobs(limit = 50, offset = 0): Promise<AdminJobsResponse> {
    const token = await getBearer();
    return apiFetch<AdminJobsResponse>(token, `/api/admin/jobs?limit=${limit}&offset=${offset}`);
  }

  async function adminGetJob(jobId: string): Promise<AdminJob> {
    const token = await getBearer();
    return apiFetch<AdminJob>(token, `/api/admin/jobs/${jobId}`);
  }

  async function adminGetStats(): Promise<AdminStatsResponse> {
    const token = await getBearer();
    return apiFetch<AdminStatsResponse>(token, '/api/admin/stats');
  }

  // ── Admin Exchange Catalog methods ──────────────────────────────────────────

  async function listExchangeCatalog(): Promise<Exchange[]> {
    const token = await getBearer();
    return apiFetch<Exchange[]>(token, '/api/exchanges/catalog');
  }

  async function adminListExchanges(): Promise<Exchange[]> {
    const token = await getBearer();
    return apiFetch<Exchange[]>(token, '/api/admin/exchanges');
  }

  async function adminUpdateExchange(id: string, data: UpdateExchangeRequest): Promise<Exchange> {
    const token = await getBearer();
    return apiFetch<Exchange>(token, `/api/admin/exchanges/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // ── User Exchange Connection methods ────────────────────────────────────────

  async function listUserExchanges(): Promise<UserExchange[]> {
    const token = await getBearer();
    return apiFetch<UserExchange[]>(token, '/api/exchanges');
  }

  async function testExchangeConnection(
    data: TestConnectionRequest,
  ): Promise<TestConnectionResponse> {
    const token = await getBearer();
    return apiFetch<TestConnectionResponse>(token, '/api/exchanges/test-connection', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async function connectExchange(data: ConnectExchangeRequest): Promise<UserExchange> {
    const token = await getBearer();
    return apiFetch<UserExchange>(token, '/api/exchanges', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async function deleteUserExchange(id: string): Promise<void> {
    const token = await getBearer();
    await apiFetch(token, `/api/exchanges/${id}`, { method: 'DELETE' });
  }

  async function syncExchange(id: string): Promise<SyncStatusResponse> {
    const token = await getBearer();
    return apiFetch<SyncStatusResponse>(token, `/api/exchanges/${id}/sync`, { method: 'POST' });
  }

  async function getSyncStatus(id: string): Promise<SyncStatusResponse> {
    const token = await getBearer();
    return apiFetch<SyncStatusResponse>(token, `/api/exchanges/${id}/sync/status`);
  }

  async function streamImportJob(
    jobId: string,
    onEvent: (event: ImportJobSSEEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const token = await getBearer();
    const res = await fetch(`${API_BASE}/api/imports/${jobId}/stream`, {
      headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
      signal,
    });
    if (!res.ok || !res.body) throw new ApiError(res.status, 'SSE stream failed');
    await parseSSEStream(res.body, 'status', (data) =>
      onEvent(JSON.parse(data) as ImportJobSSEEvent),
    );
  }

  async function streamSyncJob(
    userExchangeId: string,
    syncJobId: string,
    onEvent: (event: SyncJobSSEEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const token = await getBearer();
    const res = await fetch(
      `${API_BASE}/api/exchanges/${userExchangeId}/sync/${syncJobId}/stream`,
      { headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' }, signal },
    );
    if (!res.ok || !res.body) throw new ApiError(res.status, 'SSE stream failed');
    await parseSSEStream(res.body, 'status', (data) =>
      onEvent(JSON.parse(data) as SyncJobSSEEvent),
    );
  }

  // ── Portfolio methods ────────────────────────────────────────────────────────

  async function getPortfolio(): Promise<PortfolioPosition[]> {
    const token = await getBearer();
    return apiFetch<PortfolioPosition[]>(token, '/api/portfolio');
  }

  async function getPortfolioByExchange(): Promise<PortfolioByExchange[]> {
    const token = await getBearer();
    return apiFetch<PortfolioByExchange[]>(token, '/api/portfolio/by-exchange');
  }

  async function getPortfolioSummary(): Promise<PortfolioSummary> {
    const token = await getBearer();
    return apiFetch<PortfolioSummary>(token, '/api/portfolio/summary');
  }

  async function getPortfolioLive(): Promise<PortfolioLiveResponse> {
    const token = await getBearer();
    return apiFetch<PortfolioLiveResponse>(token, '/api/portfolio/live');
  }

  async function getPortfolioSummaryLive(): Promise<PortfolioSummaryLive> {
    const token = await getBearer();
    return apiFetch<PortfolioSummaryLive>(token, '/api/portfolio/summary/live');
  }

  async function adminGetFeatureFlags(): Promise<FeatureFlag[]> {
    const token = await getBearer();
    return apiFetch<FeatureFlag[]>(token, '/api/admin/feature-flags');
  }

  async function adminUpdateFeatureFlag(key: string, isEnabled: boolean): Promise<FeatureFlag> {
    const token = await getBearer();
    return apiFetch<FeatureFlag>(token, `/api/admin/feature-flags/${encodeURIComponent(key)}`, {
      method: 'PATCH',
      body: JSON.stringify({ isEnabled }),
    });
  }

  async function getProfile(): Promise<UserProfile> {
    const token = await getBearer();
    return apiFetch<UserProfile>(token, '/api/profile');
  }

  async function updateProfile(preferredCurrency: string): Promise<UserProfile> {
    const token = await getBearer();
    return apiFetch<UserProfile>(token, '/api/profile', {
      method: 'PATCH',
      body: JSON.stringify({ preferredCurrency }),
    });
  }

  async function completeOnboarding(preferredCurrency: string): Promise<void> {
    const token = await getBearer();
    await apiFetch<{ ok: boolean }>(token, '/api/onboarding/complete', {
      method: 'POST',
      body: JSON.stringify({ preferredCurrency }),
    });
  }

  async function calculateTax(taxYear: number): Promise<TaxCalculationResult> {
    const token = await getBearer();
    return apiFetch<TaxCalculationResult>(token, `/api/tax/calculate?taxYear=${taxYear}`);
  }

  async function startTaxJob(taxYear: number): Promise<TaxJobsResponse> {
    const token = await getBearer();
    return apiFetch<TaxJobsResponse>(token, '/api/tax/jobs', {
      method: 'POST',
      body: JSON.stringify({ taxYear }),
    });
  }

  return useMemo(
    () => ({
      createJob,
      getUploadUrl,
      uploadFile,
      commitJob,
      getStatus,
      getResults,
      downloadUrl,
      adminGetJobs,
      adminGetJob,
      adminGetStats,
      listExchangeCatalog,
      adminListExchanges,
      adminUpdateExchange,
      listUserExchanges,
      testExchangeConnection,
      connectExchange,
      deleteUserExchange,
      syncExchange,
      getSyncStatus,
      streamImportJob,
      streamSyncJob,
      getPortfolio,
      getPortfolioByExchange,
      getPortfolioSummary,
      getPortfolioLive,
      getPortfolioSummaryLive,
      adminGetFeatureFlags,
      adminUpdateFeatureFlag,
      getProfile,
      updateProfile,
      completeOnboarding,
      calculateTax,
      startTaxJob,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getToken],
  );
}
